/**
 * components/ui/AppButton.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The dashboard's one button. A thin React face on the `.lk-action-btn` family
 * in assets/css/lusuku-overrides.css (the #083860 action colour chosen on
 * 26-27 Sep 2026), so a button looks the same whether a view wrote the class
 * or used this.
 *
 *   variant  primary (navy fill)       the one main action in a place
 *            outline (navy fill)       a secondary action; FILLED since 13 Oct
 *                                      2026, every dashboard button is filled
 *            quiet   (navy fill)       Cancel, Close, Edit-type controls, also filled
 *            danger  (red fill)        a destructive control in a row
 *            danger-solid (red fill)   the confirm button of a destructive dialog
 *            warning (yellow fill)     the confirm button of a reversible-but-serious dialog
 *   size     md (36px, default) | sm (28px, table rows)
 *   icon     a Font Awesome class ("fa fa-plus"); icon-only when no children
 *   loading  keeps the label, adds a spinner, disables, sets aria-busy
 */
import React, { forwardRef } from "react";
import "./ui.css";

const VARIANT_CLASS = {
  primary: "",
  outline: "lk-action-btn--outline",
  quiet: "lk-action-btn--quiet",
  danger: "lk-action-btn--danger",
  "danger-solid": "lk-action-btn--danger-solid",
  warning: "lk-action-btn--warning",
};

/**
 * The class list for one button, shared with AppLinkButton so a control that
 * navigates and a control that acts are painted by the same rule.
 */
export const appButtonClassName = ({ variant = "primary", size = "md", iconOnly = false, className } = {}) =>
  [
    "lk-action-btn",
    size === "md" ? "lk-action-btn--lg" : "",
    VARIANT_CLASS[variant] ?? "",
    iconOnly ? "lk-action-btn--icon" : "",
    className || "",
  ].filter(Boolean).join(" ");

const AppButton = forwardRef(function AppButton(
  { variant = "primary", size = "md", icon, loading = false, disabled, className, children, type = "button", ...rest },
  ref
) {
  const iconOnly = Boolean(icon) && (children === undefined || children === null || children === false);
  return (
    <button
      ref={ref}
      type={type}
      className={appButtonClassName({ variant, size, iconOnly, className })}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="lk-btn-spinner" aria-hidden="true" /> : icon ? <i className={icon} aria-hidden="true" /> : null}
      {children}
    </button>
  );
});

export default AppButton;
